import axios from "axios"

import Choices from "choices.js"
import { debounce } from "lodash"
import { getElements } from "@oddcamp/js-utils/src/selector"

import turboInit from "../utils/turbo-init"

let instances = []

// Immutable snapshot of initial options per element (taken BEFORE Choices init)
const initialChoicesMap = new WeakMap()

const options = {
  removeItems: true,
  removeItemButton: true,
  shouldSort: false, // important so it doesn't break nested options
  searchResultLimit: 20,
  noResultsText: `Inget resultat`,
}

const destroy = () => {
  instances.forEach((inst) => {
    try {
      inst.choices.destroy()
      inst.element.outerHTML = inst.html // because Choices.destroy() doesn't work as it should (doesn't restore element to it's default form)
    } catch (error) {
      //
    }
  })
}

const handleCustomContactPersonSelect = (element) => {
  const form = element.closest(`form`) || document
  const sharedClass = `.js--choices-shared-contact-options`

  // Ensure we attach ONE coordinator per form
  if (!form._sharedChoicesCoordinatorBound) {
    form._sharedChoicesCoordinatorBound = true
    let syncing = false

    const getGroup = () =>
      Array.from(form.querySelectorAll(sharedClass)).filter((el) => el.Choices)

    const getSelectedArray = (el) => {
      const val = el.Choices.getValue(true)
      return Array.isArray(val)
        ? val.map(String)
        : [val].filter(Boolean).map(String)
    }

    const getSelectedSet = (el) => new Set(getSelectedArray(el))

    const buildListFromSnapshot = (el, selectedAcross) => {
      const base = initialChoicesMap.get(el) || []
      const ownSelected = getSelectedSet(el)
      // Rebuild from immutable base, DO NOT mark selected here
      return base.map((c) => {
        const val = String(c.value ?? ``)
        const isBlank = c.placeholder === true || val === ``
        const conflict = !ownSelected.has(val) && selectedAcross.has(val)
        const allowBlank = !el.multiple
        return {
          value: c.value,
          label: c.label,
          // IMPORTANT: keep selected out, we'll restore after setChoices
          disabled: isBlank ? !allowBlank : conflict,
          placeholder: isBlank,
          groupId: c.groupId,
          customProperties: c.customProperties,
        }
      })
    }

    const computeSelectedAcross = () => {
      const selectedAcross = new Set()
      getGroup().forEach((el) => {
        getSelectedSet(el).forEach((v) => selectedAcross.add(v))
      })
      // Never consider blank in conflicts
      selectedAcross.delete(``)
      return selectedAcross
    }

    const disabledMapFromStore = (el) => {
      const store =
        (el.Choices._store && el.Choices._store.choices) ||
        (el.Choices._currentState && el.Choices._currentState.choices) ||
        []
      const map = new Map()
      store.forEach((c) => {
        if (typeof c.value === `undefined`) return
        map.set(String(c.value ?? ``), !!c.disabled)
      })
      return map
    }

    const sameDisabledState = (el, nextList) => {
      const curr = disabledMapFromStore(el)
      if (curr.size !== nextList.length) return false
      for (const item of nextList) {
        const val = String(item.value ?? ``)
        if (!curr.has(val)) return false
        if (curr.get(val) !== !!item.disabled) return false
      }
      return true
    }

    const replaceAndRestoreSelection = (el, nextList) => {
      // Capture current selected values (as strings)
      const selectedValues = getSelectedArray(el).filter((v) => v !== ``)

      // IMPORTANT: clear current selected items to avoid duplicates
      // This removes the rendered tokens and internal selected state.
      el.Choices.removeActiveItems()

      // Replace choices WITHOUT selected flags
      el.Choices.setChoices(nextList, `value`, `label`, true)

      // Restore selection explicitly (skip blank/placeholder)
      if (selectedValues.length > 0) {
        if (el.multiple) {
          selectedValues.forEach((v) => el.Choices.setChoiceByValue(String(v)))
        } else {
          el.Choices.setChoiceByValue(String(selectedValues[0]))
        }
      }
    }

    const rebuildTarget = (targetEl) => {
      if (syncing || !targetEl || !targetEl.Choices) return
      syncing = true
      try {
        const selectedAcross = computeSelectedAcross()
        const nextList = buildListFromSnapshot(targetEl, selectedAcross)
        if (!sameDisabledState(targetEl, nextList)) {
          replaceAndRestoreSelection(targetEl, nextList)
        }
      } finally {
        syncing = false
      }
    }

    const rebuildOthers = (sourceEl) => {
      if (syncing) return
      syncing = true
      try {
        const selectedAcross = computeSelectedAcross()
        getGroup()
          .filter((el) => el !== sourceEl)
          .forEach((el) => {
            const nextList = buildListFromSnapshot(el, selectedAcross)
            if (!sameDisabledState(el, nextList)) {
              replaceAndRestoreSelection(el, nextList)
            }
          })
      } finally {
        syncing = false
      }
    }

    // Delegate change handling: rebuild only the other shared selects
    form.addEventListener(
      `change`,
      (e) => {
        const src = e.target
        if (!src || !src.matches(sharedClass)) return
        Promise.resolve().then(() => rebuildOthers(src))
      },
      true
    )

    // When a dropdown is about to show, rebuild just that select
    form.addEventListener(
      `showDropdown`,
      (e) => {
        const src = e.target
        if (!src || !src.matches(sharedClass)) return
        Promise.resolve().then(() => rebuildTarget(src))
      },
      true
    )

    // Initial pass: rebuild each once
    Promise.resolve().then(() => {
      getGroup().forEach((el) => rebuildTarget(el))
    })
  }
}

// Choices hides the original <select> and renders its own combobox, so the
// `<label for>` ends up pointing at an element the user never reaches and the
// visible control has no accessible name. `labelId` is how Choices is told
// which label names it; Rails labels have no id of their own, so give it one.
const labelIdFor = (element) => {
  if (!element.id) return undefined

  const label = document.querySelector(`label[for="${element.id}"]`)
  if (!label) return undefined

  if (!label.id) label.id = `${element.id}_label`

  return label.id
}

const create = (selector, options) => {
  getElements(selector).forEach((element) => {
    // Prevent double-initialization if element matches multiple selectors
    if (element.Choices || element.dataset.choicesInitialized === `1`) return

    // Take a CLEAN snapshot of original <option>s BEFORE Choices transforms the DOM
    const origOptions = Array.from(element.options || [])
    const seen = new Set()
    let placeholderKept = false
    const initialSnapshot = []
    for (const opt of origOptions) {
      const val = String(opt.value ?? ``)
      const isBlank = val === ``
      if (isBlank) {
        if (placeholderKept) continue
        placeholderKept = true
      } else {
        if (seen.has(val)) continue
        seen.add(val)
      }
      initialSnapshot.push({
        value: opt.value,
        label: opt.text,
        placeholder: isBlank,
        groupId: undefined,
        customProperties: undefined,
      })
    }
    initialChoicesMap.set(element, initialSnapshot)

    const html = element.outerHTML
    const labelId = labelIdFor(element)
    const choices = new Choices(
      element,
      labelId ? { ...options, labelId } : options
    )
    element.Choices = choices
    // Choices only puts `aria-labelledby` on the combobox container; its cloned
    // search input gets no name when the blank placeholder option has no text.
    if (labelId) choices.input.element.setAttribute(`aria-labelledby`, labelId)
    element.dataset.choicesInitialized = `1`
    instances.push({ element, html, choices })

    const checkHasValue = () => {
      const hasValue = (choices.getValue(true) || []).length > 0
      const floatingLabelEl =
        element.parentElement.parentElement.parentElement.parentElement
      if (
        !floatingLabelEl.classList.contains(`js--floating-form-label`) &&
        !floatingLabelEl.classList.contains(`js--floating-choices-label`)
      )
        return

      if (hasValue) {
        floatingLabelEl.classList.add(`-has-value`)
      } else {
        floatingLabelEl.classList.remove(`-has-value`)
      }
    }

    element.addEventListener(`change`, () => {
      checkHasValue()
    })

    element.addEventListener(`focus`, () => {
      const floatingLabelEl =
        element.parentElement.parentElement.parentElement.parentElement

      if (
        !floatingLabelEl.classList.contains(`js--floating-form-label`) &&
        !floatingLabelEl.classList.contains(`js--floating-choices-label`)
      )
        return
      floatingLabelEl.classList.add(`-has-value`)
    })

    element.addEventListener(`blur`, () => {
      const hasValue = (choices.getValue(true) || []).length > 0
      const floatingLabelEl =
        element.parentElement.parentElement.parentElement.parentElement
      if (
        !floatingLabelEl.classList.contains(`js--floating-form-label`) &&
        !floatingLabelEl.classList.contains(`js--floating-choices-label`)
      )
        return

      if (!hasValue.value) {
        const floatingLabelEl =
          element.parentElement.parentElement.parentElement.parentElement
        floatingLabelEl.classList.remove(`-has-value`)
      }
    })

    checkHasValue()

    if (element.dataset.choicesAjax) {
      setupAjaxChoice(element, choices)
    }

    if (element.matches(`.js--choices-shared-contact-options`)) {
      handleCustomContactPersonSelect(element)
    }
  })
}

const setupAjaxChoice = (element, choices) => {
  let request = null
  let prevValue = ``
  let valuesRestored = false

  const setNoResultsMsg = (msg) => {
    const el = choices.choiceList.element.querySelector(`.has-no-results`)
    if (el) el.innerHTML = msg
  }

  const restoreValues = (values) => {
    // choices.setChoices(values || defaultValues, `value`, `label`, true)
    valuesRestored = true
  }

  const onSearch = debounce((value) => {
    if (value.length < 3) {
      if (!valuesRestored) restoreValues()
      return
    }
    valuesRestored = false

    const csrfToken = document.querySelector(`meta[name=csrf-token]`).content

    axios.defaults.headers.common[`X-CSRF-Token`] = csrfToken

    axios({
      method: `get`,
      url: `/api/v1/${element.dataset.choicesAjax}.json`, // prettier-ignore
      params: {
        q: encodeURIComponent(value),
      },
    })
      .then(function (response) {
        if (response.data && response.data.length) {
          if (element.multiple) {
            response.data = response.data.filter(
              (i) => !choices.getValue(true).includes(i.id)
            )
          }
          choices.setChoices(response.data, `id`, `name`, true)
        } else {
          choices.setChoices([])
          setNoResultsMsg(`Inget resultat`)
        }
      })
      .then(function () {
        request = null
      })

    // Intercept request
    axios.interceptors.request.use(
      function (config, xhr) {
        if (request) {
          request.abort()
          request = null
        }
        request = xhr
        return config
      },
      function (error) {
        return Promise.reject(error)
      }
    )
  }, 500)

  if (choices.input) {
    choices.input.element.addEventListener(`keyup`, (e) => {
      const value = choices.input.element.value.trim()
      if (value != prevValue) {
        prevValue = value
        setNoResultsMsg(`Laddar…`)
        onSearch(value)
      }
    })
  }
}

const createAll = () => {
  create(`.js--choices`, options)
  create(`.js--choices-shared-contact-options`, {
    ...options,
    allowHTML: true,
  })

  create(`.js--choices-no-blank`, {
    ...options,
    removeItems: false,
    removeItemButton: false,
  })

  create(`.js--choices-no-duplicate`, {
    ...options,
    duplicateItemsAllowed: false,
  })

  create(`.js--choices-with-html`, {
    ...options,
    allowHTML: true,
  })
}

turboInit(() => {
  instances = []
  createAll()
})

export { destroy, createAll }
